// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MITs
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class FontAwesomeFallbackService {
  
  private readonly FONT_AWESOME_PUBLIC_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
  private fallbackStyleElement: HTMLLinkElement | null = null;
  private isPublicCdnLoaded = false;

  // Mapping from Kit Font Awesome classes to free public CDN classes
  private readonly iconMap = new Map<string, string>([
    // Navigation and UI
    ['fa-home', 'fa-solid fa-house'],
    ['fa-house', 'fa-solid fa-house'],
    ['fa-dashboard', 'fa-solid fa-chart-line'],
    ['fa-tachometer-alt', 'fa-solid fa-gauge'],
    ['fa-bars', 'fa-solid fa-bars'],
    ['fa-menu', 'fa-solid fa-bars'],
    ['fa-times', 'fa-solid fa-xmark'],
    ['fa-close', 'fa-solid fa-xmark'],
    ['fa-x', 'fa-solid fa-xmark'],
    ['fa-chevron-left', 'fa-solid fa-chevron-left'],
    ['fa-chevron-right', 'fa-solid fa-chevron-right'],
    ['fa-chevron-up', 'fa-solid fa-chevron-up'],
    ['fa-chevron-down', 'fa-solid fa-chevron-down'],
    ['fa-chevron-double-left', 'fa-solid fa-angles-left'],
    ['fa-chevron-double-right', 'fa-solid fa-angles-right'],
    ['fa-arrow-left', 'fa-solid fa-arrow-left'],
    ['fa-arrow-right', 'fa-solid fa-arrow-right'],
    ['fa-arrow-up', 'fa-solid fa-arrow-up'],
    ['fa-arrow-down', 'fa-solid fa-arrow-down'],
    ['fa-caret-down', 'fa-solid fa-caret-down'],
    ['fa-caret-up', 'fa-solid fa-caret-up'],
    ['fa-caret-left', 'fa-solid fa-caret-left'],
    ['fa-caret-right', 'fa-solid fa-caret-right'],
    
    // Actions
    ['fa-plus', 'fa-solid fa-plus'],
    ['fa-add', 'fa-solid fa-plus'],
    ['fa-minus', 'fa-solid fa-minus'],
    ['fa-edit', 'fa-solid fa-pen-to-square'],
    ['fa-pen', 'fa-solid fa-pen-to-square'],
    ['fa-pencil', 'fa-solid fa-pencil'],
    ['fa-pencil-alt', 'fa-solid fa-pen-to-square'],
    ['fa-trash', 'fa-solid fa-trash'],
    ['fa-delete', 'fa-solid fa-trash'],
    ['fa-save', 'fa-solid fa-floppy-disk'],
    ['fa-copy', 'fa-solid fa-copy'],
    ['fa-cut', 'fa-solid fa-scissors'],
    ['fa-paste', 'fa-solid fa-paste'],
    ['fa-download', 'fa-solid fa-download'],
    ['fa-upload', 'fa-solid fa-upload'],
    ['fa-share', 'fa-solid fa-share'],
    ['fa-export', 'fa-solid fa-file-export'],
    ['fa-import', 'fa-solid fa-file-import'],
    ['fa-print', 'fa-solid fa-print'],
    ['fa-refresh', 'fa-solid fa-rotate'],
    ['fa-sync', 'fa-solid fa-arrows-rotate'],
    ['fa-redo', 'fa-solid fa-redo'],
    ['fa-undo', 'fa-solid fa-undo'],
    
    // Additional edit variations
    ['fa-pen-square', 'fa-solid fa-pen-to-square'],
    ['fa-edit-alt', 'fa-solid fa-pen-to-square'],
    ['fa-modify', 'fa-solid fa-pen-to-square'],
    
    // Font Awesome Light (fal) to Solid mappings
    ['fa-arrow-to-bottom', 'fa-solid fa-download'],
    
    // Status and alerts
    ['fa-check', 'fa-solid fa-check'],
    ['fa-check-circle', 'fa-solid fa-circle-check'],
    ['fa-times-circle', 'fa-solid fa-circle-xmark'],
    ['fa-exclamation', 'fa-solid fa-exclamation'],
    ['fa-exclamation-triangle', 'fa-solid fa-triangle-exclamation'],
    ['fa-warning', 'fa-solid fa-triangle-exclamation'],
    ['fa-info', 'fa-solid fa-info'],
    ['fa-info-circle', 'fa-solid fa-circle-info'],
    ['fa-question', 'fa-solid fa-question'],
    ['fa-question-circle', 'fa-solid fa-circle-question'],
    ['fa-bell', 'fa-solid fa-bell'],
    ['fa-bell-slash', 'fa-solid fa-bell-slash'],
    
    // User and people
    ['fa-user', 'fa-solid fa-user'],
    ['fa-user-circle', 'fa-solid fa-circle-user'],
    ['fa-users', 'fa-solid fa-users'],
    ['fa-team', 'fa-solid fa-users'],
    ['fa-user-plus', 'fa-solid fa-user-plus'],
    ['fa-user-minus', 'fa-solid fa-user-minus'],
    ['fa-user-edit', 'fa-solid fa-user-pen'],
    ['fa-edit-user', 'fa-solid fa-user-pen'],
    ['fa-user-cog', 'fa-solid fa-user-gear'],
    ['fa-user-shield', 'fa-solid fa-user-shield'],
    ['fa-crown', 'fa-solid fa-crown'],
    
    // Communication
    ['fa-envelope', 'fa-solid fa-envelope'],
    ['fa-mail', 'fa-solid fa-envelope'],
    ['fa-email', 'fa-solid fa-envelope'],
    ['fa-phone', 'fa-solid fa-phone'],
    ['fa-comment', 'fa-solid fa-comment'],
    ['fa-comments', 'fa-solid fa-comments'],
    ['fa-chat', 'fa-solid fa-comment'],
    ['fa-message', 'fa-solid fa-message'],
    
    // Files and documents
    ['fa-file', 'fa-solid fa-file'],
    ['fa-file-alt', 'fa-solid fa-file-lines'],
    ['fa-folder', 'fa-solid fa-folder'],
    ['fa-folder-open', 'fa-solid fa-folder-open'],
    ['fa-document', 'fa-solid fa-file'],
    ['fa-pdf', 'fa-solid fa-file-pdf'],
    ['fa-image', 'fa-solid fa-image'],
    ['fa-photo', 'fa-solid fa-image'],
    ['fa-video', 'fa-solid fa-video'],
    ['fa-music', 'fa-solid fa-music'],
    ['fa-archive', 'fa-solid fa-box-archive'],
    
    // Settings and tools
    ['fa-cog', 'fa-solid fa-gear'],
    ['fa-gear', 'fa-solid fa-gear'],
    ['fa-settings', 'fa-solid fa-gear'],
    ['fa-wrench', 'fa-solid fa-wrench'],
    ['fa-tools', 'fa-solid fa-screwdriver-wrench'],
    ['fa-hammer', 'fa-solid fa-hammer'],
    ['fa-screwdriver', 'fa-solid fa-screwdriver'],
    ['fa-key', 'fa-solid fa-key'],
    ['fa-lock', 'fa-solid fa-lock'],
    ['fa-unlock', 'fa-solid fa-unlock'],
    ['fa-shield', 'fa-solid fa-shield'],
    ['fa-security', 'fa-solid fa-shield-halved'],
    
    // Data and analytics
    ['fa-chart-bar', 'fa-solid fa-chart-column'],
    ['fa-chart-line', 'fa-solid fa-chart-line'],
    ['fa-chart-pie', 'fa-solid fa-chart-pie'],
    ['fa-analytics', 'fa-solid fa-chart-line'],
    ['fa-graph', 'fa-solid fa-chart-line'],
    ['fa-table', 'fa-solid fa-table'],
    ['fa-list', 'fa-solid fa-list'],
    ['fa-database', 'fa-solid fa-database'],
    ['fa-server', 'fa-solid fa-server'],
    
    // Time and calendar
    ['fa-calendar', 'fa-solid fa-calendar'],
    ['fa-calendar-alt', 'fa-solid fa-calendar-days'],
    ['fa-clock', 'fa-solid fa-clock'],
    ['fa-time', 'fa-solid fa-clock'],
    ['fa-history', 'fa-solid fa-clock-rotate-left'],
    ['fa-stopwatch', 'fa-solid fa-stopwatch'],
    ['fa-timer', 'fa-solid fa-timer'],
    
    // Location and maps
    ['fa-map', 'fa-solid fa-map'],
    ['fa-map-marker', 'fa-solid fa-location-dot'],
    ['fa-location', 'fa-solid fa-location-dot'],
    ['fa-pin', 'fa-solid fa-thumbtack'],
    ['fa-globe', 'fa-solid fa-globe'],
    ['fa-world', 'fa-solid fa-earth-americas'],
    
    // Technology and development
    ['fa-code', 'fa-solid fa-code'],
    ['fa-laptop', 'fa-solid fa-laptop'],
    ['fa-desktop', 'fa-solid fa-desktop'],
    ['fa-mobile', 'fa-solid fa-mobile-screen'],
    ['fa-tablet', 'fa-solid fa-tablet-screen-button'],
    ['fa-wifi', 'fa-solid fa-wifi'],
    ['fa-signal', 'fa-solid fa-signal'],
    ['fa-bluetooth', 'fa-brands fa-bluetooth'],
    ['fa-usb', 'fa-brands fa-usb'],
    ['fa-plug', 'fa-solid fa-plug'],
    ['fa-battery', 'fa-solid fa-battery-full'],
    ['fa-power-off', 'fa-solid fa-power-off'],
    
    // Finance and business
    ['fa-dollar-sign', 'fa-solid fa-dollar-sign'],
    ['fa-money', 'fa-solid fa-money-bill'],
    ['fa-credit-card', 'fa-solid fa-credit-card'],
    ['fa-bank', 'fa-solid fa-building-columns'],
    ['fa-building', 'fa-solid fa-building'],
    ['fa-industry', 'fa-solid fa-industry'],
    ['fa-store', 'fa-solid fa-store'],
    ['fa-shopping-cart', 'fa-solid fa-cart-shopping'],
    ['fa-cart', 'fa-solid fa-cart-shopping'],
    
    // Transport
    ['fa-car', 'fa-solid fa-car'],
    ['fa-truck', 'fa-solid fa-truck'],
    ['fa-plane', 'fa-solid fa-plane'],
    ['fa-ship', 'fa-solid fa-ship'],
    ['fa-train', 'fa-solid fa-train'],
    
    // Social and brands
    ['fa-heart', 'fa-solid fa-heart'],
    ['fa-like', 'fa-solid fa-thumbs-up'],
    ['fa-thumbs-up', 'fa-solid fa-thumbs-up'],
    ['fa-thumbs-down', 'fa-solid fa-thumbs-down'],
    ['fa-star', 'fa-solid fa-star'],
    ['fa-bookmark', 'fa-solid fa-bookmark'],
    ['fa-flag', 'fa-solid fa-flag'],
    ['fa-tag', 'fa-solid fa-tag'],
    ['fa-tags', 'fa-solid fa-tags'],
    
    // Misc common icons
    ['fa-search', 'fa-solid fa-magnifying-glass'],
    ['fa-filter', 'fa-solid fa-filter'],
    ['fa-sort', 'fa-solid fa-sort'],
    ['fa-sort-up', 'fa-solid fa-sort-up'],
    ['fa-sort-down', 'fa-solid fa-sort-down'],
    ['fa-eye', 'fa-solid fa-eye'],
    ['fa-eye-slash', 'fa-solid fa-eye-slash'],
    ['fa-visibility', 'fa-solid fa-eye'],
    ['fa-hidden', 'fa-solid fa-eye-slash'],
    ['fa-link', 'fa-solid fa-link'],
    ['fa-external-link', 'fa-solid fa-arrow-up-right-from-square'],
    ['fa-paperclip', 'fa-solid fa-paperclip'],
    ['fa-clip', 'fa-solid fa-paperclip'],
    ['fa-magic', 'fa-solid fa-wand-magic-sparkles'],
    ['fa-wand', 'fa-solid fa-wand-magic'],
    ['fa-puzzle-piece', 'fa-solid fa-puzzle-piece'],
    ['fa-lightbulb', 'fa-solid fa-lightbulb'],
    ['fa-idea', 'fa-solid fa-lightbulb'],
    ['fa-trophy', 'fa-solid fa-trophy'],
    ['fa-award', 'fa-solid fa-award'],
    ['fa-medal', 'fa-solid fa-medal'],
    ['fa-gift', 'fa-solid fa-gift'],
    ['fa-surprise', 'fa-solid fa-gift'],
  ]);

  /**
   * Load the public Font Awesome CDN as fallback
   */
  public async loadPublicFontAwesome(): Promise<void> {
    if (this.isPublicCdnLoaded || this.fallbackStyleElement) {
      return; // Already loaded
    }

    try {
      // Create link element for Font Awesome CSS
      this.fallbackStyleElement = document.createElement('link');
      this.fallbackStyleElement.rel = 'stylesheet';
      this.fallbackStyleElement.href = this.FONT_AWESOME_PUBLIC_CDN;
      this.fallbackStyleElement.crossOrigin = 'anonymous';
      
      // Add to document head
      document.head.appendChild(this.fallbackStyleElement);
      
      // Wait for stylesheet to load
      await new Promise<void>((resolve, reject) => {
        if (this.fallbackStyleElement) {
          this.fallbackStyleElement.onload = () => {
            this.isPublicCdnLoaded = true;
            console.log('FontAwesome Fallback: Public CDN loaded successfully');
            resolve();
          };
          this.fallbackStyleElement.onerror = () => {
            console.warn('FontAwesome Fallback: Failed to load public CDN');
            reject(new Error('Failed to load Font Awesome public CDN'));
          };
        } else {
          reject(new Error('Failed to create style element'));
        }
      });
    } catch (error) {
      console.error('FontAwesome Fallback: Error loading public CDN:', error);
      this.isPublicCdnLoaded = false;
    }
  }

  /**
   * Remove the public Font Awesome CDN
   */
  public removePublicFontAwesome(): void {
    if (this.fallbackStyleElement) {
      document.head.removeChild(this.fallbackStyleElement);
      this.fallbackStyleElement = null;
      this.isPublicCdnLoaded = false;
      console.log('FontAwesome Fallback: Public CDN removed');
    }
  }

  /**
   * Get Font Awesome fallback class for a Font Awesome icon class
   */
  public getFallbackClassForIcon(faClass: string): string | null {
    // Remove 'fa-' prefix if present and normalize (includes Light fa-l)
    const normalizedClass = faClass.replace(/^fa[srl]?\s+/, '').replace(/^fa-/, '');
    
    // Try exact match first
    let fallbackClass = this.iconMap.get(`fa-${normalizedClass}`);
    if (fallbackClass) return fallbackClass;
    
    // Try without fa- prefix
    fallbackClass = this.iconMap.get(normalizedClass);
    if (fallbackClass) return fallbackClass;
    
    // Try to find partial matches for compound classes
    for (const [key, value] of this.iconMap.entries()) {
      if (normalizedClass.includes(key.replace('fa-', '')) || key.includes(normalizedClass)) {
        return value;
      }
    }
    
    // Default fallback icon for unmapped icons
    return 'fa-solid fa-file';
  }

  /**
   * Extract Font Awesome classes from an element's className
   */
  public extractFontAwesomeClasses(className: string): string[] {
    if (!className) return [];
    
    const classes = className.split(/\s+/);
    return classes.filter(cls => 
      cls.startsWith('fa-') || 
      cls === 'fas' || 
      cls === 'far' || 
      cls === 'fab' || 
      cls === 'fal' ||
      cls === 'fad'
    );
  }

  /**
   * Get the most appropriate fallback class for multiple Font Awesome classes
   */
  public getFallbackClassForClasses(classes: string[]): string {
    const faClasses = this.extractFontAwesomeClasses(classes.join(' '));
    
    // Try to find fallback class for each class, return the first match
    for (const faClass of faClasses) {
      if (!faClass.match(/^fa[srblad]?$/)) { // Skip base fa classes
        const fallbackClass = this.getFallbackClassForIcon(faClass);
        if (fallbackClass && fallbackClass !== 'fa-solid fa-file') { // Don't return default fallback unless necessary
          return fallbackClass;
        }
      }
    }
    
    return 'fa-solid fa-file'; // Default fallback
  }

  /**
   * Check if the public CDN is loaded
   */
  public isPublicCdnReady(): boolean {
    return this.isPublicCdnLoaded;
  }
}

